import React from "react";
import { cn } from "@/lib/utils";
import { ChevronUp, ChevronDown, MoreHorizontal, Loader2 } from "lucide-react";
import { EmptyState } from "@/components/ui/EmptyState";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import type { DataTableProps } from "./DataTable.types";

export const DataTable: React.FC<DataTableProps> = ({
  columns = [],
  data = [],
  loading = false,
  emptyTitle = "No data found",
  emptyDescription = "There are no items to display at this time.",
  emptyIcon,
  sortable = true,
  onSort,
  sortColumn,
  sortDirection = "asc",
  rowActions,
  onRowClick,
  getRowClassName,
  className,
}) => {
  const handleSort = (columnKey: string) => {
    if (!sortable || !onSort) return;
    const newDirection =
      sortColumn === columnKey && sortDirection === "asc" ? "desc" : "asc";
    onSort(columnKey, newDirection);
  };

  if (loading) {
    return (
      <div
        data-testid="data-table-loading"
        className="flex items-center justify-center py-12"
      >
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (!data.length) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        icon={emptyIcon}
      />
    );
  }

  return (
    <div
      data-testid="data-table"
      className={cn(
        "w-full overflow-auto rounded-lg border border-slate-200 dark:border-slate-800",
        className,
      )}
    >
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-slate-50 dark:bg-slate-800/50">
            {columns.map((column) => (
              <th
                key={column.key}
                data-testid={`table-header-${column.key}`}
                onClick={() =>
                  column.sortable !== false && handleSort(column.key)
                }
                className={cn(
                  "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider",
                  "text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700",
                  column.sortable !== false &&
                    sortable &&
                    "cursor-pointer hover:text-slate-700 dark:hover:text-slate-300",
                  column.width,
                )}
                style={{ width: column.width }}
              >
                <div className="flex items-center gap-1">
                  {column.label}
                  {sortable &&
                    column.sortable !== false &&
                    sortColumn === column.key &&
                    (sortDirection === "asc" ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    ))}
                </div>
              </th>
            ))}
            {rowActions && (
              <th className="w-12 px-4 py-3 border-b border-slate-200 dark:border-slate-700" />
            )}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => {
            const rowRecord = row as Record<string, unknown>;
            return (
              <tr
                key={String(rowRecord.id ?? rowIndex)}
                data-testid={`table-row-${rowIndex}`}
                onClick={() => onRowClick?.(rowRecord)}
                className={cn(
                  "border-b border-slate-100 dark:border-slate-800",
                  "bg-white dark:bg-slate-900",
                  "hover:bg-slate-50 dark:hover:bg-slate-800/50",
                  "transition-colors duration-100",
                  onRowClick && "cursor-pointer",
                  getRowClassName?.(rowRecord, rowIndex),
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    data-testid={`table-cell-${column.key}-${rowIndex}`}
                    className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300"
                  >
                    {column.render
                      ? column.render(
                          rowRecord[column.key],
                          rowRecord,
                          rowIndex,
                        )
                      : String(rowRecord[column.key] ?? "")}
                  </td>
                ))}
                {rowActions && (
                  <td className="px-4 py-3">
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button
                          data-testid={`table-row-actions-${rowIndex}`}
                          className={cn(
                            "p-1 rounded-md",
                            "hover:bg-slate-200 dark:hover:bg-slate-700",
                            "text-slate-500 dark:text-slate-400",
                            "transition-colors duration-150",
                          )}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          data-testid={`table-row-actions-menu-${rowIndex}`}
                          className={cn(
                            "min-w-[160px] py-1 rounded-md z-50",
                            "bg-white dark:bg-slate-900",
                            "border border-slate-200 dark:border-slate-800",
                            "shadow-lg animate-fade-in",
                          )}
                          align="end"
                          sideOffset={4}
                        >
                          {rowActions.map((action) => (
                            <DropdownMenu.Item
                              key={action.key}
                              data-testid={`table-action-${action.key}-${rowIndex}`}
                              onClick={() => action.onClick?.(rowRecord)}
                              className={cn(
                                "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer",
                                "hover:bg-slate-50 dark:hover:bg-slate-800",
                                "outline-none",
                                action.variant === "danger"
                                  ? "text-rose-600 dark:text-rose-400"
                                  : "text-slate-700 dark:text-slate-300",
                              )}
                            >
                              {action.icon && (
                                <action.icon className="w-4 h-4" />
                              )}
                              {action.label}
                            </DropdownMenu.Item>
                          ))}
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
